// ----- Phase phase-ui-draggable-modals -----
//
// Sharon, 2026-07-16: popup windows were pinned to the centre of the screen, so
// you couldn't move one aside to read the table underneath. This hook adds
// desktop-style "drag the window by its title bar".
//
// Design notes:
//   * The modal stays a MODAL — the dimmed backdrop, overlay-click and z-index
//     are untouched. This moves the window; it does not un-modal it.
//   * The window keeps its flex-centred position; dragging applies a CSS
//     `transform: translate(...)` offset from that centre. So "reset to centred"
//     is simply offset {0,0} — which is the initial state on every mount, and
//     these modals mount fresh on each open.
//   * Only the element that spreads `handleProps` initiates a drag, so body
//     text stays selectable (`user-select: none` is applied only while an
//     actual drag is in flight).
//   * Pointer Events (not mouse events) so pen/touch work where supported;
//     `touch-action: none` on the handle stops the browser scrolling instead.
//   * The window is clamped into the viewport — see `clampOffset`.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent,
} from 'react';

export interface Point {
  readonly x: number;
  readonly y: number;
}

export interface RectLike {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

export interface ViewportLike {
  readonly width: number;
  readonly height: number;
}

/** How much of the window must stay on screen, in px. */
export const MIN_VISIBLE_PX = 48;

function isHandleControl(target: PointerEvent<HTMLElement>['target']): boolean {
  return target instanceof globalThis.Element && target.closest(
    'button, a, input, select, textarea, [role="button"], [contenteditable="true"]',
  ) !== null;
}

function clamp(value: number, min: number, max: number): number {
  // Degenerate range (window larger than the viewport): prefer `min`, which
  // keeps the top/left edge — and therefore the title bar — reachable.
  if (min > max) return min;
  return Math.min(Math.max(value, min), max);
}

/**
 * Constrain a drag offset so the window can never be thrown off-screen.
 *
 * `base` is the window's rect at offset {0,0} (i.e. its centred position).
 * Horizontally at least `margin` px must remain visible on whichever side the
 * window is pushed toward. Vertically the window may never go above the top of
 * the viewport — otherwise the title bar leaves the screen and the window can
 * no longer be grabbed back.
 *
 * Pure + exported so the bounds are unit-testable without a DOM.
 */
export function clampOffset(
  desired: Point,
  base: RectLike,
  viewport: ViewportLike,
  margin: number = MIN_VISIBLE_PX,
): Point {
  const minX = margin - base.left - base.width;
  const maxX = viewport.width - margin - base.left;
  const minY = -base.top;
  const maxY = viewport.height - margin - base.top;
  return { x: clamp(desired.x, minX, maxX), y: clamp(desired.y, minY, maxY) };
}

interface DragState {
  readonly pointerId: number;
  readonly startX: number;
  readonly startY: number;
  readonly startOffset: Point;
  /** The window's rect at offset {0,0}, captured once when the drag starts. */
  readonly baseRect: RectLike;
}

export interface DraggableModalHandleProps {
  readonly onPointerDown: (e: PointerEvent<HTMLElement>) => void;
  readonly onPointerMove: (e: PointerEvent<HTMLElement>) => void;
  readonly onPointerUp: (e: PointerEvent<HTMLElement>) => void;
  readonly onPointerCancel: (e: PointerEvent<HTMLElement>) => void;
  /** phase-draggable-resizable-editor-modal — double-click the title bar
   *  resets to the default (centred, default-size) geometry. */
  readonly onDoubleClick?: () => void;
  readonly style: CSSProperties;
}

// ----- phase-ui-preview-modal-pdf-height-resize — corner resize -----

export interface SizeLike {
  readonly width: number;
  readonly height: number;
}

/** Smallest the window may be dragged down to. */
export const DEFAULT_MIN_SIZE: SizeLike = { width: 480, height: 420 };

/** Margin kept between a maximally-resized window and the viewport edge. */
export const RESIZE_VIEWPORT_MARGIN = 24;

/**
 * Constrain a resize so the window stays between the minimum size and
 * near-full-screen (viewport minus a margin on each side).
 *
 * Pure + exported so the bounds are unit-testable without a DOM.
 */
export function clampSize(
  desired: SizeLike,
  min: SizeLike,
  viewport: ViewportLike,
  margin: number = RESIZE_VIEWPORT_MARGIN,
): SizeLike {
  const maxW = Math.max(min.width, viewport.width - margin * 2);
  const maxH = Math.max(min.height, viewport.height - margin * 2);
  return {
    width: clamp(desired.width, min.width, maxW),
    height: clamp(desired.height, min.height, maxH),
  };
}

interface ResizeState {
  readonly pointerId: number;
  readonly startX: number;
  readonly startY: number;
  readonly startSize: SizeLike;
  /** Offset when the resize started — compensated as the size changes so the
   *  window's top-left corner stays pinned (see onResizePointerMove). */
  readonly startOffset: Point;
}

// ----- phase-draggable-resizable-editor-modal — persistence + small-viewport -----

/**
 * Geometry remembered across opens when `persistKey` is set: the drag offset
 * from centre and the explicit size (null = default CSS sizing).
 */
export interface PersistedGeometry {
  readonly offset: Point;
  readonly size: SizeLike | null;
}

/** localStorage namespace; matches the app's per-browser preference
 *  convention (`useShowOriginalFilenames`, ReviewClassificationsCTA). */
export const GEOMETRY_LS_PREFIX = 'autoectd.modalGeometry.';

/**
 * Below this, drag/resize/persistence are disabled and the consumer should
 * render its near-full-screen fallback: a freely-draggable tiny box on a
 * phone-sized viewport is worse than a centred sheet.
 */
export const SMALL_VIEWPORT = { width: 720, height: 560 } as const;

export function isSmallViewport(viewport: ViewportLike): boolean {
  return viewport.width < SMALL_VIEWPORT.width || viewport.height < SMALL_VIEWPORT.height;
}

/** Parse a persisted-geometry JSON string defensively; null on any shape mismatch. */
export function parsePersistedGeometry(raw: string | null): PersistedGeometry | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as {
      offset?: { x?: unknown; y?: unknown };
      size?: { width?: unknown; height?: unknown } | null;
    };
    if (typeof v?.offset?.x !== 'number' || typeof v.offset.y !== 'number') return null;
    const size =
      v.size && typeof v.size.width === 'number' && typeof v.size.height === 'number'
        ? { width: v.size.width, height: v.size.height }
        : null;
    return { offset: { x: v.offset.x, y: v.offset.y }, size };
  } catch {
    return null;
  }
}

/**
 * Clamp a remembered geometry into the CURRENT viewport before applying it.
 * The screen may have shrunk since the geometry was saved — a position that
 * was fine on the old monitor must never restore to somewhere unreachable.
 *
 * `base` is the window's rect at offset {0,0} with its default CSS size.
 * The persisted offset is relative to the CENTRED position — but restoring
 * an explicit size recentres the flex base, so the offset is COMPENSATED by
 * the base shift to keep the window's absolute on-screen position, then
 * clamped. With no persisted size the base doesn't move and the offset
 * applies as-is (clamped).
 *
 * Pure + exported so the clamp-back is unit-testable without a DOM.
 */
export function restoreGeometry(
  persisted: PersistedGeometry,
  base: RectLike,
  viewport: ViewportLike,
  min: SizeLike = DEFAULT_MIN_SIZE,
): PersistedGeometry {
  const size = persisted.size ? clampSize(persisted.size, min, viewport) : null;
  if (!size) {
    return { offset: clampOffset(persisted.offset, base, viewport), size: null };
  }
  // With an explicit size the flex base recentres for THAT size. The saved
  // offset is already relative to the size-adjusted base (resize pins the
  // top-left by compensating the offset as it goes — see
  // onResizePointerMove), so on the same viewport it applies verbatim; the
  // clamp only bites when the screen has shrunk since.
  const effectiveBase: RectLike = {
    left: Math.max(0, (viewport.width - size.width) / 2),
    top: Math.max(0, (viewport.height - size.height) / 2),
    width: size.width,
    height: size.height,
  };
  return { offset: clampOffset(persisted.offset, effectiveBase, viewport), size };
}

export interface DraggableModal {
  /** Attach to the draggable window element. */
  readonly windowRef: (el: HTMLElement | null) => void;
  /** Spread onto the window element's style. */
  readonly windowStyle: CSSProperties;
  /** Spread onto the title bar — the ONLY element that starts a drag. */
  readonly handleProps: DraggableModalHandleProps;
  readonly dragging: boolean;
  /** Current offset from the centred position. {0,0} = centred. */
  readonly offset: Point;
  /** Spread onto the bottom-right resize grip. */
  readonly resizeHandleProps: DraggableModalHandleProps;
  readonly resizing: boolean;
  /**
   * Explicit window size once the user has resized; null = default CSS
   * sizing. Like `offset`, it lives in component state: it persists across
   * re-renders/tab switches while the modal is open, and resets to the
   * default on the next open (these modals mount fresh each time).
   */
  readonly size: SizeLike | null;
  /**
   * phase-draggable-resizable-editor-modal — true when the viewport is too
   * small for free-floating geometry. Drag/resize/persistence are inert; the
   * consumer should render near-full-screen, centred, and hide the grip.
   */
  readonly smallViewport: boolean;
  /** Reset to the default geometry (centred, default CSS size) and forget
   *  any persisted geometry. Also fired by double-clicking the title bar. */
  readonly reset: () => void;
}

export interface DraggableModalOptions {
  /** Resize lower bound; defaults to DEFAULT_MIN_SIZE (480×420). */
  readonly minSize?: SizeLike;
  /**
   * phase-draggable-resizable-editor-modal — opt-in geometry persistence.
   * When set, size + position are remembered across opens under
   * `autoectd.modalGeometry.<persistKey>` (per-browser localStorage, the
   * app's preference convention) and restored CLAMPED into the current
   * viewport on the next mount. Leave unset for the original
   * fresh-geometry-per-open behaviour.
   */
  readonly persistKey?: string;
}

export function useDraggableModal(options: DraggableModalOptions = {}): DraggableModal {
  const minSize = options.minSize ?? DEFAULT_MIN_SIZE;
  const persistKey = options.persistKey;
  // Fresh {0,0} on every mount — these modals mount on open, so each open is
  // re-centred (§2.1 "position resets to centered on each open").
  const [offset, setOffset] = useState<Point>({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const elRef = useRef<HTMLElement | null>(null);
  const dragRef = useRef<DragState | null>(null);
  // phase-ui-preview-modal-pdf-height-resize — null until the user resizes;
  // then an explicit px size that overrides the default CSS sizing.
  const [size, setSize] = useState<SizeLike | null>(null);
  const [resizing, setResizing] = useState(false);
  const resizeRef = useRef<ResizeState | null>(null);

  // ── Small-viewport fallback (phase-draggable-resizable-editor-modal) ────
  const [smallViewport, setSmallViewport] = useState<boolean>(() =>
    isSmallViewport({ width: globalThis.innerWidth, height: globalThis.innerHeight }),
  );
  useEffect(() => {
    const onResize = (): void => {
      setSmallViewport(
        isSmallViewport({ width: globalThis.innerWidth, height: globalThis.innerHeight }),
      );
    };
    globalThis.addEventListener('resize', onResize);
    return () => globalThis.removeEventListener('resize', onResize);
  }, []);

  // ── Persistence (phase-draggable-resizable-editor-modal) ────────────────
  // Refs so pointer-up handlers can save without re-binding on every move.
  const offsetRef = useRef(offset);
  offsetRef.current = offset;
  const sizeRef = useRef(size);
  sizeRef.current = size;
  const smallRef = useRef(smallViewport);
  smallRef.current = smallViewport;

  const save = useCallback((): void => {
    if (!persistKey || smallRef.current) return;
    try {
      localStorage.setItem(
        `${GEOMETRY_LS_PREFIX}${persistKey}`,
        JSON.stringify({ offset: offsetRef.current, size: sizeRef.current }),
      );
    } catch {
      /* storage full / disabled — persistence is best-effort */
    }
  }, [persistKey]);

  const reset = useCallback((): void => {
    setOffset({ x: 0, y: 0 });
    setSize(null);
    if (persistKey) {
      try {
        localStorage.removeItem(`${GEOMETRY_LS_PREFIX}${persistKey}`);
      } catch {
        /* best-effort */
      }
    }
  }, [persistKey]);

  // Restore happens in the ref callback (below): it needs the element's
  // real centred rect, which only exists once the window has mounted.
  const restoredRef = useRef(false);

  const windowRef = useCallback(
    (el: HTMLElement | null): void => {
      elRef.current = el;
      if (!el || restoredRef.current || !persistKey || smallRef.current) return;
      restoredRef.current = true;
      let raw: string | null = null;
      try {
        raw = localStorage.getItem(`${GEOMETRY_LS_PREFIX}${persistKey}`);
      } catch {
        /* storage disabled */
      }
      const persisted = parsePersistedGeometry(raw);
      if (!persisted) return;
      // At this point offset is {0,0} and no explicit size is applied, so
      // the element's rect IS the centred default-size base rect.
      const rect = el.getBoundingClientRect();
      const restored = restoreGeometry(
        persisted,
        { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
        { width: globalThis.innerWidth, height: globalThis.innerHeight },
        minSize,
      );
      setOffset(restored.offset);
      setSize(restored.size);
    },
    [persistKey, minSize],
  );

  const onPointerDown = useCallback(
    (e: PointerEvent<HTMLElement>): void => {
      // Primary button only. `> 0` rather than `!== 0` so an environment that
      // omits `button` on a synthetic pointer event still drags, while a
      // secondary/middle click (1, 2) is still correctly ignored.
      if (e.button > 0) return;
      // Header controls must retain their pointer sequence. Capturing it on
      // the title bar retargets the eventual click away from the Close button.
      if (isHandleControl(e.target)) return;
      // Small-viewport fallback: the window is a centred near-full-screen
      // sheet; free dragging is disabled.
      if (smallRef.current) return;
      const el = elRef.current;
      if (!el) return;
      // Stop the browser from starting a text selection / native drag.
      e.preventDefault();

      const rect = el.getBoundingClientRect();
      dragRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        startOffset: offset,
        // Back out the current transform to get the untransformed (centred) rect.
        baseRect: {
          left: rect.left - offset.x,
          top: rect.top - offset.y,
          width: rect.width,
          height: rect.height,
        },
      };
      try {
        e.currentTarget.setPointerCapture?.(e.pointerId);
      } catch {
        /* jsdom / unsupported — move events still reach the handle */
      }
      setDragging(true);
    },
    [offset],
  );

  const onPointerMove = useCallback((e: PointerEvent<HTMLElement>): void => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const desired: Point = {
      x: drag.startOffset.x + (e.clientX - drag.startX),
      y: drag.startOffset.y + (e.clientY - drag.startY),
    };
    setOffset(
      clampOffset(desired, drag.baseRect, {
        width: globalThis.innerWidth,
        height: globalThis.innerHeight,
      }),
    );
  }, []);

  const endDrag = useCallback(
    (e: PointerEvent<HTMLElement>): void => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== e.pointerId) return;
      try {
        e.currentTarget.releasePointerCapture?.(e.pointerId);
      } catch {
        /* jsdom / unsupported */
      }
      dragRef.current = null;
      setDragging(false);
      save();
    },
    [save],
  );

  // ── Resize (phase-ui-preview-modal-pdf-height-resize) ──────────────────
  const onResizePointerDown = useCallback((e: PointerEvent<HTMLElement>): void => {
    if (e.button > 0) return;
    if (smallRef.current) return;
    const el = elRef.current;
    if (!el) return;
    e.preventDefault();
    // The grip must never also start a drag (it sits inside the window, not
    // the title bar, but stop propagation for safety).
    e.stopPropagation();
    const rect = el.getBoundingClientRect();
    resizeRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      startSize: { width: rect.width, height: rect.height },
      startOffset: offsetRef.current,
    };
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch {
      /* jsdom / unsupported */
    }
    setResizing(true);
  }, []);

  const onResizePointerMove = useCallback(
    (e: PointerEvent<HTMLElement>): void => {
      const rs = resizeRef.current;
      if (!rs || rs.pointerId !== e.pointerId) return;
      const desired: SizeLike = {
        width: rs.startSize.width + (e.clientX - rs.startX),
        height: rs.startSize.height + (e.clientY - rs.startY),
      };
      const clamped = clampSize(desired, minSize, {
        width: globalThis.innerWidth,
        height: globalThis.innerHeight,
      });
      setSize(clamped);
      // The window is flex-centred, so a size change recentres its base and
      // would drag the whole window with it (growing taller used to push the
      // title bar above the viewport). Compensate by half the delta so the
      // TOP-LEFT corner stays pinned — the desktop-standard behaviour for a
      // bottom-right grip, and what makes the persisted offset meaningful
      // independent of size.
      setOffset({
        x: rs.startOffset.x + (clamped.width - rs.startSize.width) / 2,
        y: rs.startOffset.y + (clamped.height - rs.startSize.height) / 2,
      });
    },
    [minSize],
  );

  const endResize = useCallback(
    (e: PointerEvent<HTMLElement>): void => {
      const rs = resizeRef.current;
      if (!rs || rs.pointerId !== e.pointerId) return;
      try {
        e.currentTarget.releasePointerCapture?.(e.pointerId);
      } catch {
        /* jsdom / unsupported */
      }
      resizeRef.current = null;
      setResizing(false);
      save();
    },
    [save],
  );

  // Small-viewport fallback: no transform, no explicit size — the window
  // falls back to its default CSS (which should be near-full-screen at these
  // widths, e.g. `width: min(720px, 96vw)`), centred by the overlay flexbox.
  const effectiveOffset = smallViewport ? { x: 0, y: 0 } : offset;
  const effectiveSize = smallViewport ? null : size;

  return {
    windowRef,
    windowStyle: {
      transform: `translate(${effectiveOffset.x}px, ${effectiveOffset.y}px)`,
      // The grip is absolutely positioned against the window.
      position: 'relative',
      // Only while actually dragging/resizing — body text stays selectable
      // otherwise.
      ...(dragging || resizing ? { userSelect: 'none' as const } : {}),
      // An explicit user-chosen size overrides the component's default CSS
      // sizing INCLUDING its max caps — otherwise maxWidth/maxHeight would
      // silently stop the resize partway.
      ...(effectiveSize
        ? {
            width: effectiveSize.width,
            height: effectiveSize.height,
            maxWidth: 'none' as const,
            maxHeight: 'none' as const,
          }
        : {}),
    },
    handleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onDoubleClick: reset,
      style: {
        cursor: smallViewport ? 'default' : 'move',
        touchAction: 'none',
        userSelect: 'none',
      },
    },
    dragging,
    offset: effectiveOffset,
    resizeHandleProps: {
      onPointerDown: onResizePointerDown,
      onPointerMove: onResizePointerMove,
      onPointerUp: endResize,
      onPointerCancel: endResize,
      style: { cursor: 'nwse-resize', touchAction: 'none', userSelect: 'none' },
    },
    resizing,
    size: effectiveSize,
    smallViewport,
    reset,
  };
}

/**
 * The shared bottom-right resize grip. Rendered inside the window element
 * (which the hook makes position:relative). Purely presentational — all
 * behaviour comes from spreading `resizeHandleProps`.
 */
export const RESIZE_GRIP_STYLE: CSSProperties = {
  position: 'absolute',
  right: 2,
  bottom: 2,
  width: 18,
  height: 18,
  // Three diagonal notches, the classic desktop affordance.
  background:
    'linear-gradient(135deg, transparent 0 6px, var(--border, #cbd5e1) 6px 7px, transparent 7px 10px, var(--border, #cbd5e1) 10px 11px, transparent 11px 14px, var(--border, #cbd5e1) 14px 15px, transparent 15px)',
  zIndex: 2,
};
